import React, { useState, useEffect, useRef } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';

const StateSchemeVisualization = ({ 
  countryData, 
  selectedScheme,
  selectedFinancialYear,
  selectedState,
  onStateSelect,
  stateWiseData, 
  stateWiseSchemeData ,
  showAllStates
}) => {
  
  const [hoverInfo, setHoverInfo] = useState(null);
  const geoJsonRef = useRef();
  const map = useMap();
  const hoverRef = useRef();
  const hoverTimeoutRef = useRef(null);
  const currentHoveredLayerRef = useRef(null);

  // Function to extract short form from scheme name
  const getShortForm = (schemeName) => {
    if (!schemeName) return '';
    
    const parenMatch = schemeName.match(/\(([^)]+)\)/);
    if (parenMatch) {
      return parenMatch[1];
    }
    
    const prefixMatch = schemeName.match(/^([A-Z]+-[A-Z]+)/);
    if (prefixMatch) {
      return prefixMatch[1];
    }
    
    const firstWord = schemeName.split(/\s+/)[0];
    if (firstWord === firstWord.toUpperCase() && firstWord.length >= 2) {
      return firstWord;
    }
    
    return String(schemeName)
      .replace(/scheme|program|yojana|mission|fund/gi, '')
      .split(/\s+/)
      .filter(Boolean)
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 4);
  };

  // Function to get color based on survey count
  const getColor = (count) => {
    const value = parseInt(count) || 0;
    if (value >= 10000) return '#bd0026';
    if (value >= 5000) return '#f03b20';
    if (value >= 1000) return '#feb24c';
    if (value >= 1) return '#ffeda0';
    return '#ffeda0'; // Make zero values visible
  };

  // Function to normalize state names for matching
  const normalizeStateName = (stateName) => {
    if (!stateName) return '';
    return stateName
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Find matching state data for a GeoJSON feature
  const findStateData = (stateName) => {
    if (!stateWiseSchemeData || !stateName) return 0;
    
    const normalizedGeoName = normalizeStateName(stateName);
    
    // Try exact match first
    if (stateWiseSchemeData[stateName] !== undefined) {
      return stateWiseSchemeData[stateName];
    }
    
    // Try normalized match
    for (const [apiStateName, count] of Object.entries(stateWiseSchemeData)) {
      const normalizedApiName = normalizeStateName(apiStateName);
      if (normalizedApiName === normalizedGeoName) {
        return count;
      }
    }
    
    return 0;
  };

  // Style function for state boundaries
  const stateStyle = (feature) => {
    if (selectedState) {
      return {
        fillColor: 'transparent',
        weight: 1,
        opacity: 0.7,
        color: 'transparent',
        dashArray: '3',
        fillOpacity: 0
      };
    }

    if (!selectedScheme || !stateWiseSchemeData) {
      return {
        fillColor: 'transparent',
        weight: 1,
        opacity: 0.7,
        color: 'transparent',
        dashArray: '3',
        fillOpacity: 0
      };
    }

    const stateName = feature.properties.STATE;
    const surveyCount = findStateData(stateName);
    
    return {
      fillColor: getColor(surveyCount),
      weight: 2,
      opacity: 1,
      color: 'white',
      dashArray: '3',
      fillOpacity: surveyCount > 0 ? 0.7 : 0.3
    };
  };
  

  // Optimized function to handle hover events
  const onEachState = (feature, layer) => {
    if (selectedState) {
      return;
    }

    if (!selectedScheme || !stateWiseSchemeData) {
      return;
    }

    const stateName = feature.properties.STATE;
    const surveyCount = findStateData(stateName);
    const formattedSurveyCount = surveyCount.toLocaleString();
    const schemeName = selectedScheme.schemeName;
    const shortForm = getShortForm(schemeName);
    const financialYear = selectedFinancialYear === "ALL" ? "All Years (Aggregated)" : selectedFinancialYear;

    // Store layer data for quick access
    layer._stateData = {
      stateName,
      surveyCount: formattedSurveyCount,
      schemeName,
      shortForm,
      financialYear,
      bounds: layer.getBounds(),
      hasData: surveyCount > 0
    };

    // Mouseover event
    layer.on('mouseover', function (e) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (currentHoveredLayerRef.current && currentHoveredLayerRef.current !== layer) {
        geoJsonRef.current.resetStyle(currentHoveredLayerRef.current);
      }

      currentHoveredLayerRef.current = layer;

      layer.setStyle({
        weight: 3,
        color: '#666',
        dashArray: '',
        fillOpacity: 0.9
      });

      if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
        layer.bringToFront();
      }

      hoverTimeoutRef.current = setTimeout(() => {
        const center = layer._stateData.bounds.getCenter();
        
        setHoverInfo({
          latlng: center,
          name: stateName,
          surveys: formattedSurveyCount,
          scheme: schemeName,
          shortForm: shortForm,
          financialYear: financialYear,
          hasData: surveyCount > 0
        });
      }, 100);
    });

    // Mouseout event
    layer.on('mouseout', function (e) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (currentHoveredLayerRef.current === layer) {
        geoJsonRef.current.resetStyle(layer);
        currentHoveredLayerRef.current = null;
      }

      setHoverInfo(null);
    });

    // Click event
    layer.on('click', function (e) {
      console.log("🗺️ State clicked:", stateName);
      
      setHoverInfo(null);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      
      currentHoveredLayerRef.current = null;
      
      if (onStateSelect) {
        onStateSelect(stateName);
      }
      
      const bounds = layer._stateData.bounds;
      if (bounds.isValid()) {
        map.fitBounds(bounds, { 
          padding: [20, 20],
          maxZoom: 7 
        });
      }
    });
  };

  // Tooltip management
  // Updated tooltip management useEffect
useEffect(() => {
  if (!hoverInfo) {
    if (hoverRef.current) {
      hoverRef.current.remove();
      hoverRef.current = null;
    }
    return;
  }

  // Create or update fixed position element - append to body for better z-index control
  if (!hoverRef.current) {
    hoverRef.current = document.createElement('div');
    hoverRef.current.className = 'state-fixed-popup';
    document.body.appendChild(hoverRef.current);
  }

  // Update content
  hoverRef.current.innerHTML = `
  <div class="state-tooltip ${hoverInfo.hasData ? 'has-data' : 'no-data'}">
    <h6 style="text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white;">${hoverInfo.name}</h6>
    <hr>
    <p><strong style="text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white;">Surveys:</strong> <span style="text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white;">${hoverInfo.surveys}</span></p>
    ${!hoverInfo.hasData ? '<p class="text-warning"><small>No survey data available</small></p>' : ''}
  </div>
`;

  // Position in the center of viewport
  hoverRef.current.style.top = '50%';
  hoverRef.current.style.left = '50%';

  return () => {
    if (hoverRef.current) {
      hoverRef.current.remove();
      hoverRef.current = null;
    }
  };
}, [hoverInfo]);
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hoverRef.current) {
        map.removeLayer(hoverRef.current);
      }
    };
  }, [map]);

  // Debug effect
  useEffect(() => {
    console.log('=== StateSchemeVisualization Debug ===');
    console.log('Selected Scheme:', selectedScheme?.schemeName);
    console.log('Selected Financial Year:', selectedFinancialYear);
    console.log('State Data Count from Parent:', stateWiseData?.length || 0);
    console.log('Heat Map Entries from Parent:', Object.keys(stateWiseSchemeData || {}).length);
    console.log('Selected State:', selectedState);
    console.log('Should Render GeoJSON:', !selectedState && selectedScheme && stateWiseSchemeData && Object.keys(stateWiseSchemeData).length > 0);
    
    // Log what data we actually have
    if (stateWiseSchemeData && Object.keys(stateWiseSchemeData).length > 0) {
      console.log('Available State Data:', Object.entries(stateWiseSchemeData).slice(0, 5));
    }
    console.log('====================================');
  }, [selectedScheme, selectedFinancialYear, stateWiseData, stateWiseSchemeData, selectedState]);

  if (!countryData) return null;

 const shouldRenderGeoJSON = selectedScheme && 
                           stateWiseSchemeData && 
                           Object.keys(stateWiseSchemeData).length > 0;
return (
  <>
    {shouldRenderGeoJSON && (
      <GeoJSON
        ref={geoJsonRef}
        key={`state-schemes-${selectedScheme?.schemeName || 'none'}-${selectedFinancialYear || 'all'}-${Object.keys(stateWiseSchemeData).length}-${selectedState || 'no-state'}`}
        data={countryData}
        style={stateStyle}
        onEachFeature={onEachState}
        interactive={true}
      />
    )}
      {/* No data message - ONLY show if parent has no data */}
      {!selectedState && selectedScheme && stateWiseData && stateWiseData.length === 0 && (
        <div className="leaflet-top leaflet-right">
          {/* <div className="leaflet-control leaflet-bar p-2 bg-warning">
            <i className="bi bi-info-circle"></i> 
            No data available for {selectedScheme.schemeName}
            {selectedFinancialYear && selectedFinancialYear !== "ALL" && ` in financial year ${selectedFinancialYear}`}
            {selectedFinancialYear === "ALL" && ` across all financial years`}
          </div> */}
        </div>
      )}

      {/* Legend */}
      {!selectedState && selectedScheme && stateWiseSchemeData && Object.keys(stateWiseSchemeData).length > 0 && (
        <div className="leaflet-bottom leaflet-right">
          <div className="leaflet-control leaflet-bar p-2 bg-white" style={{ maxWidth: '70px' }}>
            <div className="text-center mb-1" style={{ fontSize: '9px', fontWeight: 'bold' }}>Surveys</div>
            
            <div className="d-flex">
              <div style={{ position: 'relative', width: '18px', height: '70px', marginRight: '6px' }}>
                <div 
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    background: 'linear-gradient(to top, #ffeda0, #feb24c, #f03b20, #bd0026)',
                    borderRadius: '2px',
                    border: '1px solid #bbb'
                  }}
                ></div>
                
                <div style={{ position: 'absolute', left: 0, right: 0, top: '25%', height: '1px', backgroundColor: 'rgba(255,255,255,0.9)' }}></div>
                <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '1px', backgroundColor: 'rgba(255,255,255,0.9)' }}></div>
                <div style={{ position: 'absolute', left: 0, right: 0, top: '75%', height: '1px', backgroundColor: 'rgba(255,255,255,0.9)' }}></div>
              </div>
              
              <div className="d-flex flex-column justify-content-between" style={{ height: '70px', fontSize: '8px', color: '#444' }}>
                <div>10K+</div>
                <div>5K</div>
                <div>1K</div>
                <div>0</div>
              </div>
            </div>
          </div>
        </div>
      )}

    
<style>
  {`
    .state-fixed-popup {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 9999; /* Much higher z-index */
      min-width: 200px;
      max-width: 300px;
     background: transparent;
     border-radius: 12px;
      padding: 15px;
      box-shadow: 0 8px 30px rgba(0,0,0,0.3);
      border: 2px solid #4a90e2;
      text-align: center;
      animation: fadeIn 0.3s ease-in-out;
    }
    
   .state-tooltip h6 {
  margin: 0 0 8px 0;
  font-weight: bold;
  color: #151514;
  font-size: 16px;
  text-shadow: 
    -1px -1px 0 white,
    1px -1px 0 white,
    -1px 1px 0 white,
    1px 1px 0 white,
    0 0 5px rgba(0,0,0,0.5);
}
    
    .state-tooltip hr {
      margin: 8px 0;
      border: none;
      border-top: 2px solid #3498db;
      opacity: 0.6;
    }
    
    .state-tooltip p {
      margin: 6px 0;
      font-size: 14px;
      color: #34495e;
    }
    
    .state-tooltip p strong {
      color: #2c3e50;
      font-weight: 600;
    }
    
    .text-warning {
      color: #e74c3c !important;
      font-weight: 500;
      margin-top: 8px;
      padding: 4px 8px;
      background: rgba(231, 76, 60, 0.1);
      border-radius: 4px;
      display: inline-block;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
      to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
    
    /* Make sure map container has proper z-index context */
    .leaflet-container {
      position: relative;
    }
    
    /* If there's any parent element with high z-index, override it */
    .leaflet-map-pane {
      z-index: 1;
    }
    
    /* Ensure the tooltip appears above all leaflet layers */
    .leaflet-pane {
      z-index: 400 !important;
    }
    
    .state-fixed-popup {
      z-index: 10000 !important;
    }
  `}
</style>
    </>
  );
};

export default StateSchemeVisualization;